/* Pitstop Pro sticky header stabilizer (desktop).

   Superstore's stock traditional header watches .intersection-target with an
   IntersectionObserver and collapses the blue navigation by applying a negative
   margin. Because the sticky header is still in normal flow, that height change
   can make Chrome adjust scrollY (scroll anchoring). Near the trigger point the
   observer can then cross back over its own threshold, which produces the
   open/close/open/close "twitch".

   The theme CSS keeps Superstore's stock sentinel permanently intersecting, so
   the stock observer no longer makes sticky-state decisions. This file becomes
   the single sticky-state controller and uses two scroll thresholds (hysteresis):

     - scroll past COLLAPSE threshold -> compact header
     - scroll back above EXPAND threshold -> full navigation

   The gap between those thresholds is deliberately larger than the navigation
   row. A layout/scroll-anchor correction therefore cannot immediately reverse
   the state that caused it.
*/
(function () {
  'use strict';

  var DESKTOP = '(min-width: 1024px)';
  var SCROLLED = 'traditional-header--scrolled';
  var NAV_OPEN = 'site-header-nav--open';

  // Keep a little extra room beyond the stock 30vh trigger.
  var COLLAPSE_PAD = 36;
  // Extra hysteresis in addition to the measured navigation-row height.
  var HYSTERESIS_PAD = 56;
  // Never let the two thresholds get too close together.
  var MIN_HYSTERESIS = 110;

  var header;
  var nav;
  var toggle;
  var desktopQuery;
  var navHeight = 0;
  var collapseAt = 0;
  var expandAt = 0;
  var zone = null; // 'expanded' | 'collapsed'
  var ticking = false;
  var resizeTimer = null;
  var transitionTimer = null;

  function isDesktop() {
    return desktopQuery && desktopQuery.matches;
  }

  function findElements() {
    header = document.querySelector('[data-site-header]');
    nav = header && header.querySelector('[data-site-navigation]');
    toggle = header && header.querySelector('[data-menu-toggle]');
    return !!(header && nav);
  }

  function menuIsBusy() {
    if (!header) return false;
    return !!(
      header.querySelector('.navmenu-item-parent.open') ||
      document.body.classList.contains('scroll-lock') ||
      document.body.classList.contains('ps-no-scroll')
    );
  }

  function measureNav() {
    // The row may currently be display:none because NAV_OPEN was removed. Temporarily
    // read the last good value in that case rather than forcing a layout/state change.
    var rect = nav.getBoundingClientRect();
    if (rect.height > 0) navHeight = Math.round(rect.height);
    return navHeight || 54;
  }

  function computeThresholds() {
    var h = measureNav();
    var base = Math.round(window.innerHeight * 0.30);
    collapseAt = Math.max(180, base + COLLAPSE_PAD);
    var gap = Math.max(MIN_HYSTERESIS, h + HYSTERESIS_PAD);
    expandAt = Math.max(40, collapseAt - gap);
  }

  function clearTransitionTimer() {
    if (transitionTimer) {
      clearTimeout(transitionTimer);
      transitionTimer = null;
    }
  }

  function showNavigation() {
    clearTransitionTimer();

    header.classList.add(NAV_OPEN);
    header.classList.remove(SCROLLED);
    if (toggle) toggle.classList.remove('active');

    // Two RAFs mirrors Superstore's own openNavigation sequence and ensures the
    // wrapper has display:block before margin-top is animated back to zero.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        nav.style.marginTop = '0px';
      });
    });
  }

  function hideNavigation() {
    if (menuIsBusy()) return false;

    clearTransitionTimer();
    var h = measureNav();

    // Add the compact-header class before tucking the navigation away so the
    // hamburger control is already available during the transition.
    header.classList.add(SCROLLED);
    if (toggle) toggle.classList.remove('active');
    nav.style.marginTop = '-' + h + 'px';

    // Superstore normally removes NAV_OPEN on transitionend. Use a short fallback
    // timer instead so a missing transitionend can never leave the row stuck open.
    transitionTimer = setTimeout(function () {
      if (zone === 'collapsed' && nav.style.marginTop !== '0px') {
        header.classList.remove(NAV_OPEN);
      }
      transitionTimer = null;
    }, 260);

    return true;
  }

  function setZone(nextZone, force) {
    if (!force && nextZone === zone) return;

    if (nextZone === 'collapsed') {
      if (!hideNavigation()) return;
      zone = 'collapsed';
    } else {
      zone = 'expanded';
      showNavigation();
    }
  }

  function syncFromScroll(force) {
    if (!isDesktop()) {
      zone = null;
      return;
    }

    var y = Math.max(0, window.pageYOffset || document.documentElement.scrollTop || 0);

    if (force || zone === null) {
      // On initial load, choose a deterministic side of the dead band.
      setZone(y >= collapseAt ? 'collapsed' : 'expanded', true);
      return;
    }

    // Hysteresis: once collapsed we do not expand until the user has travelled
    // well above the collapse point; once expanded we do not collapse until they
    // travel well below it. Header-height corrections cannot satisfy both.
    if (zone === 'expanded' && y >= collapseAt) {
      setZone('collapsed');
    } else if (zone === 'collapsed' && y <= expandAt) {
      setZone('expanded');
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      syncFromScroll(false);
    });
  }

  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      computeThresholds();
      syncFromScroll(true);
    }, 120);
  }

  function onBreakpointChange() {
    computeThresholds();
    if (isDesktop()) {
      syncFromScroll(true);
    } else {
      zone = null;
      clearTransitionTimer();
      // Do not impose desktop inline positioning on the mobile header.
      nav.style.marginTop = '';
      header.classList.remove(SCROLLED);
    }
  }

  function init() {
    if (!findElements()) return;

    desktopQuery = window.matchMedia(DESKTOP);
    computeThresholds();
    syncFromScroll(true);

    // Superstore creates its IntersectionObserver before this deferred script runs.
    // Its one initial observer callback can still arrive on the next rendering step.
    // Re-assert our scroll-derived state just after startup so that callback can never
    // win on a page restored/reloaded partway down the document.
    window.requestAnimationFrame(function () {
      syncFromScroll(true);
    });
    setTimeout(function () {
      syncFromScroll(true);
    }, 120);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', onBreakpointChange);
    } else if (desktopQuery.addListener) {
      desktopQuery.addListener(onBreakpointChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
